import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import { isPro, isUnlimited } from '@cartwhl/common/stores/userStore';
import { StripeProduct } from '@cartwhl/common/types';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import Switch from '@cartwhl/ui/atoms/Switch';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import { customerStore, productStore, userStore } from 'stores/global';

import CurrentPlan from './CurrentPlan';
import InvoiceAndUsage from './InvoiceAndUsage';
import PlanCard, { EnterpriseCard } from './PlanCard';
import UserDetails from './UserDetails';

export default function PlanBilling() {
  const { loading: userLoading, userInfo } = useSnapshot(userStore.state);
  const {
    products,
    prices,
    loading: productsLoading,
  } = useSnapshot(productStore.state);
  const { customer } = useSnapshot(customerStore.state);

  const [requesting, setRequesting] = useState(false);
  const [isYearly, setIsYearly] = useState(false);

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  useEffect(() => {
    if (userInfo.stripeCustomerID) {
      customerStore.fetchCustomer(userInfo.stripeCustomerID);
    }
  }, [userInfo.stripeCustomerID]);

  const card = customer?.invoice_settings?.default_payment_method?.card;
  const address = customer?.address;
  const stripeProProductID = getStripeProductID('pro');
  const monthlyProPriceAsYear = prices.find(
    (price) =>
      price.product === stripeProProductID &&
      price.recurring.interval === 'month',
  );
  const yearlyProPrice = prices.find(
    (price) =>
      price.product === stripeProProductID &&
      price.recurring.interval === 'year',
  );
  let savings = null;
  if (monthlyProPriceAsYear && yearlyProPrice) {
    savings = Math.floor(
      100 *
        (1 -
          yearlyProPrice.unit_amount /
            (12 * monthlyProPriceAsYear.unit_amount)),
    );
  }

  async function requestPortalSession() {
    setRequesting(true);
    getStripePortalSession(userInfo.stripeCustomerID)
      .then(({ data }) => {
        if (typeof data !== 'undefined') {
          window.location.href = data.url;
        }
        setRequesting(false);
      })
      .catch(() => setRequesting(false));
  }

  const isBillingEnabled = isPro() || isUnlimited();

  if (userLoading || productsLoading) {
    return (
      <div className="overview max-w-(--breakpoint-md) mx-auto">
        <div className="animate-pulse">
          <div className="h-24 w-24 rounded-full bg-gray-200" />
          <div className="mt-4 h-6 w-48 bg-gray-200" />
          <div className="mt-2 h-4 w-32 bg-gray-200" />
        </div>
        <div className="mt-8">
          <div className="h-6 w-32 bg-gray-200" />
          <div className="mt-4 rounded-lg border p-6">
            <div className="h-8 w-full bg-gray-200" />
            <div className="mt-4 h-8 w-full bg-gray-200" />
          </div>
        </div>
        <div className="mt-8">
          <div className="h-6 w-32 bg-gray-200" />
          <div className="mt-4 rounded-lg border p-6">
            <div className="h-8 w-full bg-gray-200" />
            <div className="mt-4 h-8 w-full bg-gray-200" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overview max-w-(--breakpoint-md) mx-auto">
      <UserDetails userInfo={userInfo} />
      <div className="mt-8">
        <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
          Your plan
        </Text>
        <CurrentPlan />
      </div>
      <div className="mt-8">
        <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
          Billing Details
        </Text>
        <div className="border-gray-30 dark:border-gray-60 relative rounded-lg border p-6">
          {isBillingEnabled && (
            <>
              {card && (
                <div
                  className={`flex w-full flex-row flex-nowrap items-end gap-4 pb-8 ${
                    address
                      ? 'border-gray-30 dark:border-gray-60 mb-8 border-b'
                      : ''
                  }`}
                >
                  <div>
                    <Text
                      as="label"
                      variant="studio/body/medium/sm"
                      className=" flex flex-col gap-2.5 "
                    >
                      Credit card
                    </Text>
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="pb-1 pt-3 opacity-60 dark:opacity-70"
                    >
                      <span className="capitalize">{card.brand}</span> ending in{' '}
                      {card.last4}
                    </Text>
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="opacity-40 dark:opacity-50"
                    >
                      Expires {productStore.formatDigits(card.exp_month, 2)}/
                      {productStore.formatDigits(card.exp_year, 4)}
                    </Text>
                  </div>
                </div>
              )}
              {address && (
                <div className="flex w-full flex-row flex-nowrap items-end gap-4">
                  <div>
                    <Text
                      as="label"
                      variant="studio/body/medium/sm"
                      className=" flex flex-col gap-2.5 "
                    >
                      Billing address
                    </Text>
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="pb-1 pt-3 opacity-60 dark:opacity-70"
                    >
                      {address.line1}
                      <br />
                      {address.line2 && (
                        <>
                          {address.line2}
                          <br />
                        </>
                      )}
                      {address.city}, {address.state} {address.postal_code}
                      <br />
                      {address.country}
                    </Text>
                  </div>
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <Button
                  variant="secondary"
                  disabled={requesting}
                  onClick={requestPortalSession}
                >
                  Manage Billing
                </Button>
              </div>
            </>
          )}
          {!isBillingEnabled && (
            <Text
              as="p"
              variant="studio/body/medium/sm"
              className="text-gray-45 text-center"
            >
              Upgrade your plan to Pro or Advanced to add billing details
            </Text>
          )}
        </div>
      </div>
      <div className="mt-8">
        <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
          Invoices & Usage
        </Text>
        <InvoiceAndUsage />
      </div>
      <div className="mt-8">
        <div className="mb-4 flex basis-full flex-col flex-nowrap items-start">
          <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
            All plans
          </Text>
          <Switch
            className="py-2 font-medium"
            left="Pay monthly"
            right={`Pay yearly — ${savings}% off`}
            superscript={null}
            defaultValue={isYearly}
            onChange={setIsYearly}
          />
        </div>
        <div className="flex flex-col flex-wrap gap-4 sm:flex-row md:mt-8 xl:gap-6">
          {[products.free, products.pro, products.unlimited].map(
            (product, i) => (
              <PlanCard
                key={i}
                mode={isYearly ? 'year' : 'month'}
                product={product as StripeProduct}
                showCharacters={false}
              />
            ),
          )}
          <EnterpriseCard />
        </div>
      </div>
    </div>
  );
}
